/**
 * Майстер підключення POS-станції (Порція Роздріб.7 follow-up, 2026-05-23).
 *
 * Для OWNER/RETAIL_MANAGER. 6 кроків з прогрес-баром:
 *   1. Юрособа
 *   2. Каса (cash_register)
 *   3. Склад (warehouse)
 *   4. Цінова група
 *   5. ПРРО-провайдер
 *   6. POS-станція
 * Фініш — підсумок + інструкція як підключити касира у /admin.
 *
 * Принципи:
 *  - Кожен крок: dropdown існуючих або inline-create спрощеного шаблону.
 *  - State у URL-query (?step=N&org=…&cash=…&…) — F5 не губить прогрес.
 *  - Жодних edit-actions — wizard тільки створює нове або обирає існуюче.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { retailPosProviders, type PosProviderType } from '@/api/retailPosProviders';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';


const STEPS = [
  { n: 1, title: 'Юрособа', icon: '🏢' },
  { n: 2, title: 'Каса', icon: '💰' },
  { n: 3, title: 'Склад', icon: '📦' },
  { n: 4, title: 'Цінова група', icon: '🏷️' },
  { n: 5, title: 'ПРРО', icon: '📋' },
  { n: 6, title: 'POS-станція', icon: '🖥️' },
] as const;

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: unknown } } }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          const o = d as { msg?: string };
          return o.msg ?? 'invalid';
        })
        .join('; ');
    }
  }
  return String(e);
}

export default function POSSetupWizard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const step = Math.max(1, Math.min(7, Number(params.get('step') || '1')));

  // Зберігаємо обрані ID у query для F5-стабільності
  const orgId = params.get('org') || '';
  const cashRegisterId = params.get('cash') || '';
  const warehouseId = params.get('wh') || '';
  const priceGroupId = params.get('pg') || '';
  const providerId = params.get('prv') || '';
  const workstationId = params.get('ws') || '';

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const goStep = (n: number) => setParam('step', String(n));

  return (
    <div className="wizard">
      <div className="wizard__header">
        <h2 style={{ margin: 0 }}>🧙 Майстер підключення POS-станції</h2>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--text-secondary)' }}>
          Покрокове налаштування каси для нового магазину. На кожному кроці —
          оберіть існуюче або створіть нове.
        </p>
      </div>

      <WizardProgress current={step} />

      <div className="wizard__step">
        {step === 1 && (
          <Step1Organization
            orgId={orgId}
            onSelect={(id) => setParam('org', id)}
            onNext={() => goStep(2)}
          />
        )}
        {step === 2 && (
          <Step2CashRegister
            orgId={orgId}
            cashRegisterId={cashRegisterId}
            onSelect={(id) => setParam('cash', id)}
            onBack={() => goStep(1)}
            onNext={() => goStep(3)}
          />
        )}
        {step === 3 && (
          <Step3Warehouse
            orgId={orgId}
            warehouseId={warehouseId}
            onSelect={(id) => setParam('wh', id)}
            onBack={() => goStep(2)}
            onNext={() => goStep(4)}
          />
        )}
        {step === 4 && (
          <Step4PriceGroup
            orgId={orgId}
            priceGroupId={priceGroupId}
            onSelect={(id) => setParam('pg', id)}
            onBack={() => goStep(3)}
            onNext={() => goStep(5)}
          />
        )}
        {step === 5 && (
          <Step5Provider
            orgId={orgId}
            providerId={providerId}
            onSelect={(id) => setParam('prv', id)}
            onBack={() => goStep(4)}
            onNext={() => goStep(6)}
          />
        )}
        {step === 6 && (
          <Step6Workstation
            orgId={orgId}
            cashRegisterId={cashRegisterId}
            warehouseId={warehouseId}
            priceGroupId={priceGroupId}
            providerId={providerId}
            onCreated={(id) => {
              setParam('ws', id);
              goStep(7);
            }}
            onBack={() => goStep(5)}
          />
        )}
        {step === 7 && (
          <StepFinish
            workstationId={workstationId}
            onRestart={() => {
              setParams({}, { replace: true });
            }}
            onGoToPOS={() => navigate('/pos')}
            onGoToAdmin={() => navigate('/admin')}
          />
        )}
      </div>

      <Styles />
    </div>
  );
}

// ============================================================================
// Progress
// ============================================================================

function WizardProgress({ current }: { current: number }) {
  return (
    <div className="wizard__progress">
      {STEPS.map((s) => (
        <div
          key={s.n}
          className={
            'wizard__step-dot' +
            (current === s.n ? ' wizard__step-dot--active' : '') +
            (current > s.n ? ' wizard__step-dot--done' : '')
          }
        >
          <div className="wizard__step-circle">
            {current > s.n ? '✓' : s.icon}
          </div>
          <div className="wizard__step-label">
            <small>Крок {s.n}</small>
            <div>{s.title}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Step 1: Організація
// ============================================================================

function Step1Organization({
  orgId,
  onSelect,
  onNext,
}: {
  orgId: string;
  onSelect: (id: string) => void;
  onNext: () => void;
}) {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <StepCard
      title="Крок 1 з 6 · Юрособа"
      hint="Оберіть юрособу (ТОВ/ФОП), для якої будуєте магазин. Якщо ще немає — створіть нову."
    >
      <div className="wizard__field">
        <label>Юрособа</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={orgId}
            onChange={(e) => onSelect(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— оберіть —</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.code}
              </option>
            ))}
          </select>
          <button onClick={() => setCreateOpen(true)}>+ Нова юрособа</button>
        </div>
      </div>

      <StepFooter>
        <button
          className="primary"
          disabled={!orgId}
          onClick={onNext}
        >
          Далі →
        </button>
      </StepFooter>

      {createOpen && (
        <NoticeModal
          title="Створення юрособи"
          message="Створення нової юрособи робиться на сторінці «Юрособи» (sidebar → Юрособи). Поверніться сюди після створення — вона з'явиться у dropdown'і."
          actions={[
            { label: 'Закрити', onClick: () => setCreateOpen(false) },
            {
              label: 'Перейти у «Юрособи»',
              variant: 'primary',
              onClick: () => {
                window.open('/organizations', '_blank');
                setCreateOpen(false);
              },
            },
          ]}
        />
      )}
    </StepCard>
  );
}

// ============================================================================
// Step 2: Каса
// ============================================================================

function Step2CashRegister({
  orgId,
  cashRegisterId,
  onSelect,
  onBack,
  onNext,
}: {
  orgId: string;
  cashRegisterId: string;
  onSelect: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['cash-registers', orgId],
    queryFn: () => refs.cashRegisters(orgId),
    enabled: !!orgId,
  });
  // Валюта UAH з API (НЕ хардкод id — різний по оточеннях/інсталяціях).
  const currencies = useQuery({
    queryKey: ['refs-currencies'],
    queryFn: () => refs.currencies(),
  });
  const uahId = currencies.data?.find((c) => c.code === 'UAH')?.id
    ?? currencies.data?.[0]?.id ?? '';
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ code: 'KASA-01', name: 'Каса магазину' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      refs.createCashRegister({
        organization_id: orgId,
        code: form.code,
        name: form.name,
        currency_id: uahId,
        default_direction_id: null,
      }),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: ['cash-registers', orgId] });
      onSelect(cr.id);
      setCreateOpen(false);
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <StepCard
      title="Крок 2 з 6 · Каса"
      hint="Каса — джерело готівкових проводок (Дт 301). Кожен магазин/POS має свою касу."
    >
      <div className="wizard__field">
        <label>Каса (готівкова)</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={cashRegisterId}
            onChange={(e) => onSelect(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— оберіть —</option>
            {list.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.code}
              </option>
            ))}
          </select>
          <button onClick={() => setCreateOpen(true)}>+ Нова каса</button>
        </div>
        {list.data && list.data.length === 0 && (
          <small style={{ color: 'var(--text-secondary)' }}>
            У цієї юрособи ще немає кас. Натисніть «+ Нова каса».
          </small>
        )}
      </div>

      <StepFooter>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" disabled={!cashRegisterId} onClick={onNext}>
          Далі →
        </button>
      </StepFooter>

      {createOpen && (
        <InlineCreateModal
          title="Нова каса"
          onClose={() => setCreateOpen(false)}
          onSubmit={() => create.mutate()}
          isPending={create.isPending}
          error={error}
        >
          <div className="wizard__field">
            <label>Код</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              maxLength={30}
            />
          </div>
          <div className="wizard__field">
            <label>Назва</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={255}
            />
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>
            Валюта — UAH автоматично.
          </small>
        </InlineCreateModal>
      )}
    </StepCard>
  );
}

// ============================================================================
// Step 3: Склад
// ============================================================================

function Step3Warehouse({
  orgId,
  warehouseId,
  onSelect,
  onBack,
  onNext,
}: {
  orgId: string;
  warehouseId: string;
  onSelect: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId),
    enabled: !!orgId,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ code: 'SKLAD-01', name: 'Склад магазину' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      refs.createWarehouse({
        organization_id: orgId,
        code: form.code,
        name: form.name,
        warehouse_type: 'RETAIL',
        is_storage: true,
      }),
    onSuccess: (w) => {
      qc.invalidateQueries({ queryKey: ['warehouses', orgId] });
      onSelect(w.id);
      setCreateOpen(false);
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <StepCard
      title="Крок 3 з 6 · Склад"
      hint="Склад — звідки списується товар при продажу. Для роздрібного магазину — створіть тип «RETAIL»."
    >
      <div className="wizard__field">
        <label>Склад</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={warehouseId}
            onChange={(e) => onSelect(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— оберіть —</option>
            {list.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.code}
              </option>
            ))}
          </select>
          <button onClick={() => setCreateOpen(true)}>+ Новий склад</button>
        </div>
      </div>

      <StepFooter>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" disabled={!warehouseId} onClick={onNext}>
          Далі →
        </button>
      </StepFooter>

      {createOpen && (
        <InlineCreateModal
          title="Новий склад"
          onClose={() => setCreateOpen(false)}
          onSubmit={() => create.mutate()}
          isPending={create.isPending}
          error={error}
        >
          <div className="wizard__field">
            <label>Код</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              maxLength={30}
            />
          </div>
          <div className="wizard__field">
            <label>Назва</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={255}
            />
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>
            Тип — RETAIL (роздрібний) автоматично.
          </small>
        </InlineCreateModal>
      )}
    </StepCard>
  );
}

// ============================================================================
// Step 4: Цінова група
// ============================================================================

function Step4PriceGroup({
  orgId,
  priceGroupId,
  onSelect,
  onBack,
  onNext,
}: {
  orgId: string;
  priceGroupId: string;
  onSelect: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['retail-price-groups', orgId],
    queryFn: () => retailPriceGroups.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ code: 'RETAIL', name: 'Роздрібні ціни' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      retailPriceGroups.create({
        organization_id: orgId,
        code: form.code,
        name: form.name,
        is_default: true,
        is_active: true,
        sort_order: 100,
      }),
    onSuccess: (pg) => {
      qc.invalidateQueries({ queryKey: ['retail-price-groups', orgId] });
      onSelect(pg.id);
      setCreateOpen(false);
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <StepCard
      title="Крок 4 з 6 · Цінова група"
      hint="Цінова група — набір роздрібних цін для цієї POS. Кожна номенклатура матиме окрему ціну у цій групі."
    >
      <div className="wizard__field">
        <label>Цінова група</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={priceGroupId}
            onChange={(e) => onSelect(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— оберіть —</option>
            {list.data?.items.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} — {g.code}
                {g.is_default ? ' (за замовч.)' : ''}
              </option>
            ))}
          </select>
          <button onClick={() => setCreateOpen(true)}>+ Нова група</button>
        </div>
      </div>

      <StepFooter>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" disabled={!priceGroupId} onClick={onNext}>
          Далі →
        </button>
      </StepFooter>

      {createOpen && (
        <InlineCreateModal
          title="Нова цінова група"
          onClose={() => setCreateOpen(false)}
          onSubmit={() => create.mutate()}
          isPending={create.isPending}
          error={error}
        >
          <div className="wizard__field">
            <label>Код</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              maxLength={30}
            />
          </div>
          <div className="wizard__field">
            <label>Назва</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={255}
            />
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>
            Буде створена як «за замовчуванням». Ціни товарів — потім через
            «Роздрібні ціни» або масовою «Зміна цін».
          </small>
        </InlineCreateModal>
      )}
    </StepCard>
  );
}

// ============================================================================
// Step 5: ПРРО-провайдер
// ============================================================================

function Step5Provider({
  orgId,
  providerId,
  onSelect,
  onBack,
  onNext,
}: {
  orgId: string;
  providerId: string;
  onSelect: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['retail-pos-providers', orgId],
    queryFn: () => retailPosProviders.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<{
    code: string;
    name: string;
    provider_type: PosProviderType;
  }>({
    code: 'MOCK-DEMO',
    name: 'Тренувальний ПРРО (Demo)',
    provider_type: 'mock',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      retailPosProviders.create({
        organization_id: orgId,
        code: form.code,
        name: form.name,
        provider_type: form.provider_type,
        is_connected: form.provider_type === 'mock',
        is_active: true,
        config: {},
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['retail-pos-providers', orgId] });
      onSelect(p.id);
      setCreateOpen(false);
    },
    onError: (e) => setError(extractError(e)),
  });

  const testConn = useMutation({
    mutationFn: () => retailPosProviders.testConnection(providerId),
  });

  return (
    <StepCard
      title="Крок 5 з 6 · ПРРО-провайдер"
      hint="ПРРО — Програмний реєстратор розрахункових операцій. У фазі 1 використовуйте Mock (тренувальний). Реальні Checkbox/Vchasno — фаза 2."
    >
      <div className="wizard__field">
        <label>ПРРО-провайдер</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={providerId}
            onChange={(e) => onSelect(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">— оберіть —</option>
            {list.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.code} [{p.provider_type}]
              </option>
            ))}
          </select>
          <button onClick={() => setCreateOpen(true)}>+ Новий ПРРО</button>
        </div>
        {providerId && (
          <div style={{ marginTop: '0.5rem' }}>
            <button onClick={() => testConn.mutate()} disabled={testConn.isPending}>
              🔌 Тест підключення
            </button>
            {testConn.data && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  color: testConn.data.success ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {testConn.data.success ? '✓ Успіх' : '⚠ Помилка'} ·{' '}
                {testConn.data.last_test_status}
                {testConn.data.last_test_message ? `: ${testConn.data.last_test_message}` : ''}
              </span>
            )}
            {testConn.isError && (
              <span style={{ marginLeft: '0.5rem', color: 'var(--danger)' }}>
                {extractError(testConn.error)}
              </span>
            )}
          </div>
        )}
      </div>

      <StepFooter>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" disabled={!providerId} onClick={onNext}>
          Далі →
        </button>
      </StepFooter>

      {createOpen && (
        <InlineCreateModal
          title="Новий ПРРО-провайдер"
          onClose={() => setCreateOpen(false)}
          onSubmit={() => create.mutate()}
          isPending={create.isPending}
          error={error}
        >
          <div className="wizard__field">
            <label>Тип</label>
            <select
              value={form.provider_type}
              onChange={(e) =>
                setForm({ ...form, provider_type: e.target.value as PosProviderType })
              }
            >
              <option value="mock">Mock — тренувальний (фаза 1) ⭐</option>
              <option value="checkbox">Checkbox (фаза 2)</option>
              <option value="vchasno">Вчасно (фаза 2)</option>
            </select>
          </div>
          <div className="wizard__field">
            <label>Код</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              maxLength={30}
            />
          </div>
          <div className="wizard__field">
            <label>Назва</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={255}
            />
          </div>
          {form.provider_type !== 'mock' && (
            <small style={{ color: 'var(--warning)' }}>
              ⚠ Реальні Checkbox/Vchasno потребують API-токенів — додасте їх
              пізніше через сторінку «ПРРО-провайдери».
            </small>
          )}
        </InlineCreateModal>
      )}
    </StepCard>
  );
}

// ============================================================================
// Step 6: POS-станція
// ============================================================================

function Step6Workstation({
  orgId,
  cashRegisterId,
  warehouseId,
  priceGroupId,
  providerId,
  onCreated,
  onBack,
}: {
  orgId: string;
  cashRegisterId: string;
  warehouseId: string;
  priceGroupId: string;
  providerId: string;
  onCreated: (id: string) => void;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ code: 'POS-01', name: 'Каса магазину' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      retailPosWorkstations.create({
        organization_id: orgId,
        code: form.code,
        name: form.name,
        workstation_type: 'desktop',
        warehouse_id: warehouseId,
        cash_register_id: cashRegisterId,
        default_price_group_id: priceGroupId,
        pos_provider_id: providerId,
        is_active: true,
      }),
    onSuccess: (ws) => {
      qc.invalidateQueries({ queryKey: ['retail-pos-workstations'] });
      onCreated(ws.id);
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <StepCard
      title="Крок 6 з 6 · POS-станція"
      hint="Останній крок: створюємо POS-станцію з усіма прив'язками з попередніх кроків."
    >
      <div className="wizard__summary">
        <div>📦 Склад: <strong>обрано</strong></div>
        <div>💰 Каса: <strong>обрана</strong></div>
        <div>🏷️ Цінова група: <strong>обрана</strong></div>
        <div>📋 ПРРО: <strong>підключено</strong></div>
      </div>

      <div className="wizard__field">
        <label>Код POS-станції</label>
        <input
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          maxLength={30}
        />
      </div>
      <div className="wizard__field">
        <label>Назва</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          maxLength={255}
        />
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>{error}</div>
      )}

      <StepFooter>
        <button onClick={onBack}>← Назад</button>
        <button
          className="primary"
          onClick={() => create.mutate()}
          disabled={create.isPending || !form.code || !form.name}
        >
          {create.isPending ? 'Створюю...' : '✓ Створити POS-станцію'}
        </button>
      </StepFooter>
    </StepCard>
  );
}

// ============================================================================
// Step 7: Фініш
// ============================================================================

function StepFinish({
  workstationId,
  onRestart,
  onGoToPOS,
  onGoToAdmin,
}: {
  workstationId: string;
  onRestart: () => void;
  onGoToPOS: () => void;
  onGoToAdmin: () => void;
}) {
  return (
    <StepCard
      title="🎉 Готово!"
      hint="POS-станція створена і налаштована. Залишився один крок — підключити користувача-касира."
    >
      <div className="wizard__success">
        <div style={{ fontSize: '3rem', textAlign: 'center' }}>✅</div>
        <div style={{ fontSize: '1.1rem', textAlign: 'center', marginTop: '0.5rem' }}>
          POS-станція <strong>успішно створена</strong>
        </div>
        {workstationId && (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontSize: '0.85rem',
              marginTop: '0.25rem',
            }}
          >
            ID: <code>{workstationId.slice(0, 8)}…</code>
          </div>
        )}
      </div>

      <div className="wizard__next-step">
        <h3 style={{ marginTop: 0 }}>📋 Наступний крок: створіть касира</h3>
        <ol style={{ marginLeft: '1.25rem', lineHeight: '1.8' }}>
          <li>
            Перейдіть у <strong>Адміністрування акаунта</strong> (sidebar →
            «Адміністрування акаунта»).
          </li>
          <li>
            Вкладка <strong>«Користувачі і доступи»</strong> → «запросити
            користувача»: email касира, роль в акаунті — «Користувач», у
            вашій юрособі — роль <strong>«Касир POS»</strong>.
          </li>
          <li>
            Касир отримає лист із запрошенням і сам задасть пароль. Доступи
            можна змінити будь-коли — кнопка «доступи» біля користувача.
          </li>
          <li>
            Після входу касир автоматично опиниться у POS-режимі і обере цю
            POS-станцію.
          </li>
        </ol>
      </div>

      <StepFooter>
        <button onClick={onRestart}>🔄 Налаштувати ще одну</button>
        <button onClick={onGoToPOS}>🖥️ Перейти у POS (як власник)</button>
        <button className="primary" onClick={onGoToAdmin}>
          👤 Створити касира →
        </button>
      </StepFooter>
    </StepCard>
  );
}

// ============================================================================
// Shared UI
// ============================================================================

function StepCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="wizard__card">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>{hint}</p>
      {children}
    </div>
  );
}

function StepFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="wizard__footer">
      {children}
    </div>
  );
}

function InlineCreateModal({
  title,
  onClose,
  onSubmit,
  isPending,
  error,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  isPending: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="wizard__modal-overlay" onClick={onClose}>
      <div className="wizard__modal" onClick={(e) => e.stopPropagation()}>
        <div className="wizard__modal-header">
          <span>{title}</span>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="wizard__modal-body">
          {children}
          {error && (
            <div style={{ color: 'var(--danger)', marginTop: '0.5rem' }}>{error}</div>
          )}
        </div>
        <div className="wizard__modal-footer">
          <button onClick={onClose}>Скасувати</button>
          <button className="primary" onClick={onSubmit} disabled={isPending}>
            {isPending ? 'Створюю...' : 'Створити'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoticeModal({
  title,
  message,
  actions,
}: {
  title: string;
  message: string;
  actions: { label: string; onClick: () => void; variant?: 'primary' }[];
}) {
  return (
    <div className="wizard__modal-overlay">
      <div className="wizard__modal">
        <div className="wizard__modal-header">
          <span>{title}</span>
        </div>
        <div className="wizard__modal-body">{message}</div>
        <div className="wizard__modal-footer">
          {actions.map((a) => (
            <button
              key={a.label}
              className={a.variant === 'primary' ? 'primary' : ''}
              onClick={a.onClick}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Inline styles
// ============================================================================

function Styles() {
  return (
    <style>{`
      .wizard {
        max-width: 900px;
        margin: 0 auto;
        padding: 1rem;
      }
      .wizard__header {
        margin-bottom: 1.5rem;
      }
      .wizard__progress {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        margin: 1.5rem 0 2rem;
        position: relative;
      }
      .wizard__progress::before {
        content: '';
        position: absolute;
        top: 20px;
        left: 40px;
        right: 40px;
        height: 2px;
        background: var(--border);
        z-index: 0;
      }
      .wizard__step-dot {
        flex: 1;
        text-align: center;
        position: relative;
        z-index: 1;
        opacity: 0.5;
      }
      .wizard__step-dot--active {
        opacity: 1;
      }
      .wizard__step-dot--done {
        opacity: 0.85;
      }
      .wizard__step-circle {
        width: 40px;
        height: 40px;
        line-height: 40px;
        margin: 0 auto 0.5rem;
        background: var(--bg-panel);
        border: 2px solid var(--border);
        border-radius: 50%;
        font-size: 1.2rem;
      }
      .wizard__step-dot--active .wizard__step-circle {
        background: var(--primary);
        color: white;
        border-color: var(--primary);
      }
      .wizard__step-dot--done .wizard__step-circle {
        background: var(--success);
        color: white;
        border-color: var(--success);
      }
      .wizard__step-label small {
        color: var(--muted);
        display: block;
        font-size: 0.7rem;
      }
      .wizard__step-label > div {
        font-size: 0.85rem;
        font-weight: 600;
      }
      .wizard__card {
        background: var(--bg-panel);
        border: 1px solid var(--border);
        border-radius: var(--radius-lg);
        padding: 1.5rem;
        box-shadow: var(--shadow-sm);
      }
      .wizard__field {
        margin: 0.75rem 0;
      }
      .wizard__field label {
        display: block;
        font-size: 0.85rem;
        color: var(--text-secondary);
        margin-bottom: 0.25rem;
        font-weight: 600;
      }
      .wizard__field input,
      .wizard__field select {
        width: 100%;
        padding: 0.6rem;
        border: 1px solid var(--border);
        border-radius: var(--radius-sm);
        font-size: 0.95rem;
      }
      .wizard__footer {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid var(--border-soft);
      }
      .wizard__footer button.primary {
        margin-left: auto;
      }
      .wizard__summary {
        background: var(--bg-soft);
        padding: 1rem;
        border-radius: var(--radius-sm);
        margin-bottom: 1rem;
        font-size: 0.9rem;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem;
      }
      .wizard__success {
        padding: 2rem;
        background: var(--success-soft);
        border-radius: var(--radius);
        margin-bottom: 1.5rem;
      }
      .wizard__next-step {
        background: var(--primary-soft);
        padding: 1.25rem;
        border-radius: var(--radius);
        margin-bottom: 1rem;
      }
      .wizard__modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        padding: 1rem;
      }
      .wizard__modal {
        background: var(--bg-panel);
        border-radius: var(--radius-lg);
        max-width: 500px;
        width: 100%;
        box-shadow: var(--shadow-lg);
      }
      .wizard__modal-header {
        padding: 1rem;
        border-bottom: 1px solid var(--border);
        font-weight: 700;
        display: flex;
        justify-content: space-between;
      }
      .wizard__modal-header button {
        background: transparent;
        border: none;
        font-size: 1.2rem;
        cursor: pointer;
      }
      .wizard__modal-body { padding: 1rem; }
      .wizard__modal-footer {
        padding: 1rem;
        border-top: 1px solid var(--border);
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    `}</style>
  );
}
